import { FC } from 'react';

import dayjs from 'dayjs';
import { FormikProps } from 'formik';
import { Plus, Trash2 } from 'lucide-react';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { DateTimePicker } from '@mui/x-date-pickers';

import { getDescriptionMaxLength, StreamerWheelForm, STREAMER_WHEEL_BONUS_MAX_LENGTH } from '../form';
import { STREAMER_WHEEL_SELECT_OPTIONS } from '../utils';

import css from './StreamerWheelFormFields.module.css';

import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { Select } from 'v2/components/select/Select';
import { Switch } from 'v2/components/switch/Switch';

interface StreamerWheelFormFieldsProps {
  formik: FormikProps<StreamerWheelForm>;
}

export const StreamerWheelFormFields: FC<StreamerWheelFormFieldsProps> = ({ formik }) => {
  const handleSelectChange = (field: keyof StreamerWheelForm) => (value: unknown) => {
    formik.setFieldValue(field, value, true);
    formik.setFieldTouched(field, true, false);
  };

  const handleAddMetadata = () => {
    formik.setFieldValue('metadata', [...formik.values.metadata, { key: '', value: '' }]);
  };

  const handleRemoveMetadata = (index: number) => () => {
    formik.setFieldValue(
      'metadata',
      formik.values.metadata.filter((_, i) => i !== index)
    );
  };

  return (
    <>
      <div className="form-group">
        <Select
          value={formik.values.type}
          options={STREAMER_WHEEL_SELECT_OPTIONS}
          label="Type"
          onChange={handleSelectChange('type')}
          required
        />

        <TextField
          name="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={!!formik.touched.name && !!formik.errors.name}
          helperText={formik.touched.name && formik.errors.name}
          label="Name"
          required
        />
      </div>

      <FileSelector
        key={formik.values.image390Id || 'empty-image390Id'}
        value={formik.values.image390Id}
        error={formik.touched.image390Id ? formik.errors.image390Id : undefined}
        onSelect={handleSelectChange('image390Id') as (value: string) => void}
        label="Partner logo (390px)"
        required
      />

      <div className="form-group">
        <TextField
          name="link"
          value={formik.values.link}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={!!formik.touched.link && !!formik.errors.link}
          helperText={formik.touched.link && formik.errors.link}
          label="Link"
          required
          fullWidth
        />

        <TextField
          name="bonus"
          value={formik.values.bonus}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={!!formik.touched.bonus && !!formik.errors.bonus}
          helperText={formik.touched.bonus && formik.errors.bonus}
          label="Bonus"
          required
          slotProps={{ htmlInput: { maxLength: STREAMER_WHEEL_BONUS_MAX_LENGTH } }}
          fullWidth
        />
      </div>

      <TextField
        name="description"
        value={formik.values.description}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={!!formik.touched.description && !!formik.errors.description}
        helperText={formik.touched.description && formik.errors.description}
        label="Description (optional)"
        slotProps={{
          htmlInput: {
            maxLength: getDescriptionMaxLength(formik.values.type),
          },
        }}
      />

      <div className="form-group">
        <DateTimePicker
          value={formik.values.endDate}
          onChange={handleSelectChange('endDate')}
          label="End date (optional)"
          format="DD.MM.YYYY HH:mm"
          minDate={dayjs()}
          slotProps={{
            textField: {
              name: 'endDate',
              onBlur: formik.handleBlur,
              error: !!formik.touched.endDate && !!formik.errors.endDate,
              helperText: formik.touched.endDate ? formik.errors.endDate : undefined,
            },
          }}
        />

        <Switch name="isActive" checked={formik.values.isActive} onChange={formik.handleChange} label="Active" />
      </div>

      <div className={css.metadataSection}>
        <div className={css.sectionTitle}>Metadata (optional)</div>

        <div className={css.metadataList}>
          {formik.values.metadata.map((entry, index) => (
            <div key={index} className={css.metadataItem}>
              <TextField
                name={`metadata.${index}.key`}
                value={entry.key}
                onChange={formik.handleChange}
                label="Key"
                size="small"
                sx={{ flex: 1 }}
              />

              <TextField
                name={`metadata.${index}.value`}
                value={entry.value}
                onChange={formik.handleChange}
                label="Value"
                size="small"
                sx={{ flex: 1 }}
              />

              <IconButton size="small" color="error" onClick={handleRemoveMetadata(index)}>
                <Trash2 size={16} />
              </IconButton>
            </div>
          ))}

          <Button size="small" variant="outlined" startIcon={<Plus size={14} />} onClick={handleAddMetadata}>
            Add Metadata
          </Button>
        </div>
      </div>
    </>
  );
};
